import React, { useState, useEffect } from "react";
import { submitFlightResponse } from "apis/requestServices";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import { ModalProps } from "./Types";
import { useToastStore } from "store/toastStore";
import { ExclamationCircleIcon } from "@heroicons/react/20/solid";

const RequestReplyTab: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  logoImg,
  airlineName,
  flightNumber,
  flightDate,
  airlineId,
  departureAirport,
  arrivalAirport,
  departureTime,
  arrivalTime,
  flightDuration,
  requestDetailId,
}) => {
  const defaultSeatData = {
    requestDetailId: 0,
    firstClass: 0,
    businessClass: 0,
    economyPlusClass: 0,
    economyClass: 0,
    nonRevListed: 0,
    nonFirst: 0,
    nonBusiness: 0,
    nonEconomyPlus: 0,
    nonEconomy: 0,
    moreFirstClass: false,
    moreBusinessClass: false,
    moreEcoClass: false,
    moreEcoPlus: false,
    comments: "",
  };

  const [seatData, setSeatData] = useState(defaultSeatData);
  const [error, setError] = useState<string | null>(null);
  const [focusedClass, setFocusedClass] = useState<
    "First" | "Economy" | "Eco+" | "Business" | "NonRev"
  >("First");
  const [noClassChecked, setNoClassChecked] = useState({
    First: false,
    Business: false,
    Economy: false,
    "Eco+": false,
  });
  const [unknownNonRevCount, setUnknownNonRevCount] = useState(false);
  const [nonRevValidationError, setNonRevValidationError] = useState<
    string | null
  >(null);
  const [dontKnowNonRev, setDontKnowNonRev] = useState(false);

  const { showLoader, hideLoader } = useGlobalLoader();
  const [comment, setComment] = useState("");

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSeatData({
        ...defaultSeatData,
        requestDetailId: requestDetailId || 0,
      });
      setError(null);
      setNonRevValidationError(null);
      setFocusedClass("First");
      setNoClassChecked({
        First: false,
        Business: false,
        Economy: false,
        "Eco+": false,
      });
      setUnknownNonRevCount(false);
    }
  }, [isOpen, requestDetailId]);
  const handleInputChange = (
    field: string,
    value: number | boolean | string
  ) => {
    // Clear non-rev validation error if any of these fields are changed
    if (
      field === "nonRevListed" ||
      field === "nonFirst" ||
      field === "nonBusiness" ||
      field === "nonEconomyPlus" ||
      field === "nonEconomy"
    ) {
      setNonRevValidationError(null);
    }

    // Handle "NoClass" checkbox logic
    if (field === "NoClass") {
      const seatField =
        focusedClass === "First"
          ? "firstClass"
          : focusedClass === "Business"
            ? "businessClass"
            : focusedClass === "Eco+"
              ? "economyPlusClass"
              : "economyClass";
      const moreField =
        focusedClass === "First"
          ? "moreFirstClass"
          : focusedClass === "Business"
            ? "moreBusinessClass"
            : focusedClass === "Eco+"
              ? "moreEcoPlus"
              : "moreEcoClass";

      let nonRevField = "";
      if (focusedClass === "First") nonRevField = "nonFirst";
      else if (focusedClass === "Business") nonRevField = "nonBusiness";
      else if (focusedClass === "Eco+") nonRevField = "nonEconomyPlus";
      else if (focusedClass === "Economy") nonRevField = "nonEconomy";

      setNoClassChecked((prev) => ({
        ...prev,
        [focusedClass]: true,
      }));

      setSeatData((prev) => ({
        ...prev,
        [seatField]: 0,
        [moreField]: false,
        ...(nonRevField && { [nonRevField]: 0 }),
      }));
    }
    // Handle unknownNonRevCount toggle
    else if (field === "unknownNonRevCount") {
      const isChecked = value as boolean;
      setNonRevValidationError(null);
      setUnknownNonRevCount(isChecked);
      if (isChecked) {
        setSeatData((prev) => ({
          ...prev,
          nonFirst: 0,
          nonBusiness: 0,
          nonEconomyPlus: 0,
          nonEconomy: 0,
          nonRevListed: 0,
        }));
      }
    }
    // Handle changes to per-class or non-rev fields
    else {
      if (
        (field === "firstClass" && focusedClass === "First") ||
        (field === "businessClass" && focusedClass === "Business") ||
        (field === "economyPlusClass" && focusedClass === "Eco+") ||
        (field === "economyClass" && focusedClass === "Economy")
      ) {
        if (value !== null) {
          setNoClassChecked((prev) => ({
            ...prev,
            [focusedClass]: false,
          }));
        }
        if (value === "" || value === 0) {
          const nonRevField =
            field === "firstClass"
              ? "nonFirst"
              : field === "businessClass"
                ? "nonBusiness"
                : field === "economyPlusClass"
                  ? "nonEconomyPlus"
                  : "nonEconomy";
          setSeatData((prev) => ({
            ...prev,
            [field]: value,
            [nonRevField]: 0,
          }));
          return;
        }
      }

      // Auto-calculate nonRevListed if any per-class non-rev field changes
      if (
        field === "nonFirst" ||
        field === "nonBusiness" ||
        field === "nonEconomyPlus" ||
        field === "nonEconomy"
      ) {
        const updatedSeatData = {
          ...seatData,
          [field]: Number(value),
        };

        const hasAnyNonRevClassValue =
          updatedSeatData.nonFirst > 0 ||
          updatedSeatData.nonBusiness > 0 ||
          updatedSeatData.nonEconomyPlus > 0 ||
          updatedSeatData.nonEconomy > 0;

        if (!unknownNonRevCount && hasAnyNonRevClassValue) {
          const total =
            Number(updatedSeatData.nonFirst || 0) +
            Number(updatedSeatData.nonBusiness || 0) +
            Number(updatedSeatData.nonEconomyPlus || 0) +
            Number(updatedSeatData.nonEconomy || 0);

          updatedSeatData.nonRevListed = total;
        }

        setSeatData(updatedSeatData);
        return;
      }

      // Default behavior for all other fields
      setSeatData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleClose = () => {
    setSeatData(defaultSeatData);
    setError(null);
    onClose?.();
  };

  const handleSubmit = async () => {
    console.log(seatData);
    // Reset errors
    setError(null);
    setNonRevValidationError(null);

    // Only validate Non-Rev Listed if toggle is OFF
    if (!unknownNonRevCount) {
      const {
        nonRevListed,
        nonFirst,
        nonBusiness,
        nonEconomyPlus,
        nonEconomy,
      } = seatData;

      const hasNonRevFields =
        (nonFirst || 0) > 0 ||
        (nonBusiness || 0) > 0 ||
        (nonEconomyPlus || 0) > 0 ||
        (nonEconomy || 0) > 0;

      const totalNonRevPerClass =
        (nonFirst || 0) +
        (nonBusiness || 0) +
        (nonEconomyPlus || 0) +
        (nonEconomy || 0);

      // Case 1: Any per-class field is filled — enforce match with nonRevListed
      if (hasNonRevFields) {
        if (!nonRevListed || nonRevListed < 0) {
          setNonRevValidationError(
            "Please provide the number of listed Non-Rev passengers."
          );
          return;
        }

        if (totalNonRevPerClass !== nonRevListed) {
          setNonRevValidationError(
            "Total of 'Listed Non-Rev Pax Per Class' must match the 'Non Rev' value."
          );
          return;
        }
      }

      // // Case 2: nonRevListed is filled, but no per-class fields have values
      // if (nonRevListed > 0 && !hasNonRevFields) {
      //   setNonRevValidationError(
      //     "Please distribute the Non-Rev passengers across the classes above."
      //   );
      //   return;
      // }
    }

    showLoader();
    try {
      await submitFlightResponse({ ...seatData, requestDetailId, comments: comment });
      // console.log("seatdata:",seatData);
      setComment("");

      setSeatData(defaultSeatData);
      onSubmit?.();
      // Hide loader before showing success toast
      hideLoader();
      useToastStore
        .getState()
        .showToast("Request answered successfully!", "success", 3000);
    } catch (error: any) {
      // Hide loader before showing error toast
      hideLoader();
      useToastStore
        .getState()
        .showToast(error?.response?.data?.message, "error", 3000);
    }
  };

  const getInputClass = (
    value: number,
    classType: "First" | "Economy" | "Eco+" | "Business"
  ) => {
    const isNoClass = noClassChecked[classType];
    return `w-28 text-center h-14 content-center border rounded-md text-lg font-bold placeholder:text-sm placeholder:font-normal ${
      value < 0
        ? "border-red-500 bg-red-100 text-red-600"
        : "border-lightgray-100 text-black"
    } ${isNoClass ? "opacity-50 bg-gray-100" : ""}`;
  };

  const getNonRevInputClass = (value: number) => {
    return `px-5 text-center h-14 border rounded-md text-black text-lg font-bold placeholder:text-sm placeholder:font-normal ${
      unknownNonRevCount
        ? "bg-gray-100 border-gray-200 text-gray-400"
        : "border-lightgray-100"
    }`;
  };

  const showNonRevClassInputs = focusedClass === "NonRev";
  const showToggleOptions = focusedClass !== "NonRev";

  const getInputValue = (
    classType: "First" | "Economy" | "Eco+" | "Business"
  ) => {
    const isNoClass = noClassChecked[classType];
    const value =
      classType === "First"
        ? seatData.firstClass
        : classType === "Business"
          ? seatData.businessClass
          : classType === "Eco+"
            ? seatData.economyPlusClass
            : seatData.economyClass;
    if (isNoClass) return "";
    return value === 0 ? "" : value;
  };

  if (!isOpen) return null;

  return (
    <div
      id="default-modal"
      tabIndex={-1}
      aria-hidden={!isOpen}
      className="overflow-y-hidden overflow-x-hidden fixed p-8 top-0 right-0 left-0 z-50 md:inset-0 flex justify-center items-center w-full h-full bg-black bg-opacity-50"
    >
      <div className="relative p-6 w-full max-w-xl my-8">
        <div className="relative bg-white max-h-[90vh] overflow-y-auto rounded-2xl shadow">
          {/* Modal Header */}
          <div className="flex justify-between px-3 pt-6 rounded-t">
            <button
              type="button"
              className="text-gray-400 bg-transparent rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center"
              onClick={handleClose}
            >
              <svg
                className="w-3 h-3"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 14 14"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                />
              </svg>
              <span className="sr-only">Close modal</span>
            </button>
          </div>

          {/* Flight Info */}
          <div className="p-4 mt-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                className="w-10"
                src={logoImg}
                alt={airlineName}
              />
              <div>
                <p className="text-lightgray-200 text-xs">Airlines</p>
                <p className="text-grease-100 mt-1 text-sm font-medium">
                  {airlineName}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-8">
              <div>
                <p className="text-lightgray-200 text-xs">Flight No.</p>
                <p className="text-grease-100 mt-1 text-sm font-medium">
                  {flightNumber}
                </p>
              </div>
              <div>
                <p className="text-lightgray-200 text-xs">Date</p>
                <p className="text-grease-100 mt-1 text-sm font-medium">
                  {flightDate}
                </p>
              </div>
            </div>
          </div>

          {/* Departure / Arrival Visualization */}
          <div className="bg-sky-100 bg-opacity-40 rounded-xl p-4 m-4 mb-0">
            <div className="flex justify-between items-center">
              <div className="text-center">
                <p className="text-grease-100 text-sm font-medium">
                  {departureAirport}
                </p>
                <div className="flex items-center justify-center gap-1 w-fit mt-0.5">
                  <i className="st-arrow-slant text-gray-500 font-medium"></i>
                  <p className="text-lightgray-200 text-xs">{departureTime}</p>
                </div>
              </div>
              <div className="w-full flex flex-col items-center">
                <div className="flex items-center w-full">
                  <div className="w-3 h-3 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <div className="w-2 h-2 bg-white rounded-full flex items-center justify-center">
                      <div className="w-1 h-1 bg-royalblue-100 rounded-full"></div>
                    </div>
                  </div>
                  <div className="w-full border-b-2 border-dashed border-indigo-200"></div>
                  <i className="st-flight-slim text-royalblue-100 text-base"></i>
                  <div className="w-full border-b-2 border-dashed border-indigo-200"></div>
                  <div className="w-3 h-3 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <div className="w-2 h-2 bg-white rounded-full flex items-center justify-center">
                      <div className="w-1 h-1 bg-royalblue-100 rounded-full"></div>
                    </div>
                  </div>
                </div>
                <p className="text-lightgray-150 text-xs text-center mt-2">
                  {flightDuration}
                </p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <p className="text-grease-100 text-sm font-medium">
                  {arrivalAirport}
                </p>
                <div className="flex items-center justify-center gap-1 w-fit mt-0.5">
                  <i className="st-arrow-slant text-gray-500 font-medium rotate-90"></i>
                  <p className="text-lightgray-200 text-xs">{arrivalTime}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Class Inputs */}
          <div className="flex items-center justify-between gap-3 p-4">
            <input
              type="number"
              placeholder="First"
              onFocus={() => setFocusedClass("First")}
              value={getInputValue("First")}
              onChange={(e) =>
                handleInputChange(
                  "firstClass",
                  e.target.value === "" ? "" : Number(e.target.value)
                )
              }
              className={getInputClass(seatData.firstClass, "First")}
            />
            <input
              type="number"
              placeholder="Eco"
              onFocus={() => setFocusedClass("Economy")}
              value={getInputValue("Economy")}
              onChange={(e) =>
                handleInputChange(
                  "economyClass",
                  e.target.value === "" ? "" : Number(e.target.value)
                )
              }
              className={getInputClass(seatData.economyClass, "Economy")}
            />
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
            <input
              type="number"
              placeholder="Eco+"
              onFocus={() => setFocusedClass("Eco+")}
              value={getInputValue("Eco+")}
              onChange={(e) =>
                handleInputChange(
                  "economyPlusClass",
                  e.target.value === "" ? "" : Number(e.target.value)
                )
              }
              className={getInputClass(seatData.economyPlusClass, "Eco+")}
            />
            <input
              type="number"
              placeholder="Bus"
              onFocus={() => setFocusedClass("Business")}
              value={getInputValue("Business")}
              onChange={(e) =>
                handleInputChange(
                  "businessClass",
                  e.target.value === "" ? "" : Number(e.target.value)
                )
              }
              className={getInputClass(seatData.businessClass, "Business")}
            />
          </div>

          {/* Non Rev Listed Input */}
          <div className="flex flex-col items-center justify-between gap-3 mb-8 mt-2 px-6">
            <p className="font-medium text-sm text-grease-100">
              Listed Non Rev Passengers
            </p>
            <input
              type="number"
              min="0"
              placeholder="Non Rev"
              onFocus={() => setFocusedClass("NonRev")}
              value={seatData.nonRevListed || ""}
              onChange={(e) => {
                const numValue = Number(e.target.value);
                // Turn off the toggle if user starts typing
                if (unknownNonRevCount) {
                  setUnknownNonRevCount(false);
                }
                if (numValue >= 0 || e.target.value === "") {
                  handleInputChange("nonRevListed", numValue);
                }
              }}
              className={`px-5 text-center h-14 border rounded-md text-black text-lg font-bold placeholder:text-sm placeholder:font-normal ${
                unknownNonRevCount
                  ? "bg-gray-100 border-gray-200 text-gray-400"
                  : "border-lightgray-100"
              }`}
              disabled={dontKnowNonRev}
            />
            {nonRevValidationError && (
              <div className="mt-1 flex items-center gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                <ExclamationCircleIcon className="h-4 w-4 text-yellow-600" />
                <p className="text-red-600 text-xs">{nonRevValidationError}</p>
              </div>
            )}
          </div>

          {/* Non-Rev Pax Per Class Card */}
          {showNonRevClassInputs && (
            <div className="bg-sky-100 bg-opacity-40 rounded-xl mx-4">
              <p className="font-medium text-sm text-grease-100 mb-3 text-center pt-3">
                Listed Non-Rev Pax Per Class
              </p>
              <div className="flex items-center justify-between gap-3 p-2">
                <input
                  type="number"
                  min="0"
                  placeholder="First"
                  value={seatData.nonFirst === 0 ? "" : seatData.nonFirst}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    if (/^\d*$/.test(inputValue)) {
                      const numValue = parseInt(inputValue, 10);
                      handleInputChange(
                        "nonFirst",
                        inputValue === "" ? "" : numValue >= 0 ? numValue : 0
                      );
                    }
                  }}
                  className={`w-24 text-center h-14 content-center border ${seatData.firstClass !== 0 && !unknownNonRevCount ? "border-lightgray-100" : "border-gray-200 bg-gray-100"} rounded-md ${seatData.firstClass !== 0 && !unknownNonRevCount ? "text-black" : "text-gray-400"} text-lg font-bold placeholder:text-sm placeholder:font-normal`}
                  disabled={!seatData.firstClass || unknownNonRevCount}
                />
                <input
                  type="number"
                  min="0"
                  placeholder="Eco"
                  value={seatData.nonEconomy === 0 ? "" : seatData.nonEconomy}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    if (/^\d*$/.test(inputValue)) {
                      const numValue = parseInt(inputValue, 10);
                      handleInputChange(
                        "nonEconomy",
                        inputValue === "" ? "" : numValue >= 0 ? numValue : 0
                      );
                    }
                  }}
                  className={`w-24 text-center h-14 content-center border ${seatData.economyClass !== 0 && !unknownNonRevCount ? "border-lightgray-100" : "border-gray-200 bg-gray-100"} rounded-md ${seatData.economyClass !== 0 && !unknownNonRevCount ? "text-black" : "text-gray-400"} text-lg font-bold placeholder:text-sm placeholder:font-normal`}
                  disabled={!seatData.economyClass || unknownNonRevCount}
                />
                <input
                  type="number"
                  min="0"
                  placeholder="Eco+"
                  value={
                    seatData.nonEconomyPlus === 0 ? "" : seatData.nonEconomyPlus
                  }
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    if (/^\d*$/.test(inputValue)) {
                      const numValue = parseInt(inputValue, 10);
                      handleInputChange(
                        "nonEconomyPlus",
                        inputValue === "" ? "" : numValue >= 0 ? numValue : 0
                      );
                    }
                  }}
                  className={`w-24 text-center h-14 content-center border ${seatData.economyPlusClass !== 0 && !unknownNonRevCount ? "border-lightgray-100" : "border-gray-200 bg-gray-100"} rounded-md ${seatData.economyPlusClass !== 0 && !unknownNonRevCount ? "text-black" : "text-gray-400"} text-lg font-bold placeholder:text-sm placeholder:font-normal`}
                  disabled={!seatData.economyPlusClass || unknownNonRevCount}
                />
                <input
                  type="number"
                  min="0"
                  placeholder="Bus"
                  value={seatData.nonBusiness === 0 ? "" : seatData.nonBusiness}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    if (/^\d*$/.test(inputValue)) {
                      const numValue = parseInt(inputValue, 10);
                      handleInputChange(
                        "nonBusiness",
                        inputValue === "" ? "" : numValue >= 0 ? numValue : 0
                      );
                    }
                  }}
                  className={`w-24 text-center h-14 content-center border ${seatData.businessClass !== 0 && !unknownNonRevCount ? "border-lightgray-100" : "border-gray-200 bg-gray-100"} rounded-md ${seatData.businessClass !== 0 && !unknownNonRevCount ? "text-black" : "text-gray-400"} text-lg font-bold placeholder:text-sm placeholder:font-normal`}
                  disabled={!seatData.businessClass || unknownNonRevCount}
                />
              </div>
              {/* {nonRevValidationError && (
                <div className="px-4 pb-4">
                  <p className="text-red-500 text-xs text-center">{nonRevValidationError}</p>
                </div>
              )} */}
              <div className="py-4 px-4 bg-sky-200 bg-opacity-30 rounded-b-xl">
                <label className="inline-flex items-center cursor-pointer p-2">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={unknownNonRevCount}
                    onChange={(e) =>
                      handleInputChange("unknownNonRevCount", e.target.checked)
                    }
                    onClick={() => {
                      setDontKnowNonRev((prev) => !prev);
                    }}
                  />
                  <div className="relative w-11 h-6 bg-blue-100 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 peer-checked:bg-blue-600"></div>
                  <span className="ms-3 text-sm font-thin text-lightgray-200">
                    I don't know how many non-rev passengers are listed
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* Checkbox Toggle Section */}
          {showToggleOptions && (
            <div className="border border-blue-100 bg-white rounded-xl p-6 mx-6 mb-6 mt-3">
              {/* Overbooked Toggle */}
              <div className="py-4 border-b border-blue-100">
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={
                      (focusedClass === "First"
                        ? seatData.firstClass
                        : focusedClass === "Business"
                          ? seatData.businessClass
                          : focusedClass === "Eco+"
                            ? seatData.economyPlusClass
                            : seatData.economyClass) < 0
                    }
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      const seatField =
                        focusedClass === "First"
                          ? "firstClass"
                          : focusedClass === "Business"
                            ? "businessClass"
                            : focusedClass === "Eco+"
                              ? "economyPlusClass"
                              : "economyClass";
                      handleInputChange(seatField, isChecked ? -1 : 0);
                    }}
                    disabled={
                      focusedClass === "First"
                        ? seatData.firstClass === null
                        : focusedClass === "Business"
                          ? seatData.businessClass === null
                          : focusedClass === "Eco+"
                            ? seatData.economyPlusClass === null
                            : seatData.economyClass === null
                    }
                  />
                  <div className="relative w-11 h-6 bg-blue-100 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  <span className="ms-3 text-sm font-thin text-lightgray-200">
                    Overbooked
                  </span>
                </label>
              </div>
              {/* This flight has no... Toggle */}
              <div className="py-4 border-b border-blue-100">
                <label
                  className="inline-flex items-center cursor-pointer"
                >
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    id="no-class-checkbox"
                    checked={noClassChecked[focusedClass]}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      setNoClassChecked((prev) => ({
                        ...prev,
                        [focusedClass]: isChecked,
                      }));
                      if (isChecked) {
                        handleInputChange("NoClass", true);
                      } else {
                        const seatField =
                          focusedClass === "First"
                            ? "firstClass"
                            : focusedClass === "Business"
                              ? "businessClass"
                              : focusedClass === "Eco+"
                                ? "economyPlusClass"
                                : "economyClass";
                        handleInputChange(seatField, "");
                      }
                    }}
                  />
                  <div
                    className="relative w-11 h-6 bg-blue-100 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"
                  ></div>
                  <span
                    className="ms-3 text-sm font-thin text-lightgray-200"
                  >
                    This flight has no {focusedClass} class
                  </span>
                </label>
              </div>
              {/* More Seats Available Toggle */}
              <div className="py-4 border-b border-blue-100">
                <label
                  className={`inline-flex items-start ${noClassChecked[focusedClass] ? "cursor-not-allowed" : "cursor-pointer"}`}
                >
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={
                      focusedClass === "First"
                        ? seatData.moreFirstClass
                        : focusedClass === "Business"
                          ? seatData.moreBusinessClass
                          : focusedClass === "Eco+"
                            ? seatData.moreEcoPlus
                            : seatData.moreEcoClass
                    }
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      const field =
                        focusedClass === "First"
                          ? "moreFirstClass"
                          : focusedClass === "Business"
                            ? "moreBusinessClass"
                            : focusedClass === "Eco+"
                              ? "moreEcoPlus"
                              : "moreEcoClass";
                      handleInputChange(field, isChecked);
                    }}
                    disabled={
                      noClassChecked[focusedClass] ||
                      (focusedClass === "First"
                        ? seatData.firstClass === null
                        : focusedClass === "Business"
                          ? seatData.businessClass === null
                          : focusedClass === "Eco+"
                            ? seatData.economyPlusClass === null
                            : seatData.economyClass === null)
                    }
                  />
                  <div
                    className={`relative w-11 h-6 ${noClassChecked[focusedClass] ? "bg-gray-200" : "bg-blue-100"} peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${!noClassChecked[focusedClass] && "peer-checked:bg-blue-600"} flex-shrink-0 mt-0.5`}
                  ></div>
                  <span
                    className={`ms-3 text-sm font-thin ${noClassChecked[focusedClass] ? "text-gray-400" : "text-lightgray-200"}`}
                  >
                    There are more seats available in {focusedClass} class
                    <br /> but I don't know how many
                  </span>
                </label>
              </div>
            </div>
          )}

          <div className=" bg-white rounded-xl p-6 mx-6 mb-6 mt-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Comment (optional)
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a comment if needed"
              rows={3}
              className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Submit / Cancel Buttons */}
          <div className="flex gap-4 items-center justify-end p-6 rounded-b">
            <button
              onClick={handleClose}
              className="flex items-center gap-2 p-2 bg-indigo-100 rounded-lg"
            >
              <p className="font-medium text-grease-50 text-sm px-3">Cancel</p>
            </button>
            <button
              onClick={handleSubmit}
              disabled={
                seatData.economyClass == null || // disable if null
                seatData.economyClass == 0 || // disable if zero or negative (if you want to allow negative, remove this)
                (!unknownNonRevCount &&
                  (!seatData.nonRevListed || seatData.nonRevListed < 0))
              }
              className={`flex items-center gap-2 p-2 bg-blue-600 rounded-lg ${
                seatData.economyClass == null ||
                seatData.economyClass == 0 ||
                (!unknownNonRevCount &&
                  (!seatData.nonRevListed || seatData.nonRevListed < 0))
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
            >
              <p className="font-medium text-white text-sm px-3">Submit Load</p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestReplyTab;
